import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

// Empresa contable activa, compartida por toda la app. Se guarda en la URL
// (?empresaContable=ID) y no solo en memoria para que pestañas distintas del
// navegador puedan trabajar con empresas distintas al mismo tiempo, sin
// necesidad de construir soporte real de multi-ventana.
const EmpresaContableContext = createContext(null);

function leerEmpresaDeUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('empresaContable') || '';
  } catch {
    return '';
  }
}

export function EmpresaContableProvider({ children }) {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empresaActivaId, setEmpresaActivaIdState] = useState(() => {
    const deUrl = leerEmpresaDeUrl();
    if (deUrl) return deUrl;
    try { return localStorage.getItem('cont_empresa_activa') || ''; } catch { return ''; }
  });

  const recargarEmpresas = useCallback(() => {
    if (!localStorage.getItem('token')) { setEmpresas([]); return; }
    setLoading(true);
    axios.get('/api/contabilidad/empresas')
      .then(r => setEmpresas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEmpresas([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { recargarEmpresas(); }, [recargarEmpresas]);

  const setEmpresaActiva = useCallback((id) => {
    const val = id ? String(id) : '';
    setEmpresaActivaIdState(val);
    try { localStorage.setItem('cont_empresa_activa', val); } catch {}
    try {
      const url = new URL(window.location.href);
      if (val) url.searchParams.set('empresaContable', val);
      else url.searchParams.delete('empresaContable');
      window.history.replaceState(null, '', url.toString());
    } catch {}
  }, []);

  const empresaActiva = empresas.find(e => String(e.id) === String(empresaActivaId)) || null;

  // Si el usuario solo tiene una empresa contable asignada, se la asigna sola
  // (no lo hace elegir de más, igual que pedía el plan original).
  useEffect(() => {
    if (!empresaActivaId && empresas.length === 1) {
      setEmpresaActiva(empresas[0].id);
    }
  }, [empresas, empresaActivaId, setEmpresaActiva]);

  return (
    <EmpresaContableContext.Provider value={{
      empresas, empresaActivaId, empresaActiva, loading,
      setEmpresaActiva, recargarEmpresas,
    }}>
      {children}
    </EmpresaContableContext.Provider>
  );
}

const DEFAULT_CTX = { empresas: [], empresaActivaId: '', empresaActiva: null, loading: false, setEmpresaActiva: () => {}, recargarEmpresas: () => {} };

export function useEmpresaActiva() {
  const ctx = useContext(EmpresaContableContext);
  return ctx || DEFAULT_CTX;
}
